// Premio de un rol de la promo de referido: chips del tipo de premio y los campos de ese tipo.
import { useId, type ReactNode } from 'react';
import { useFormContext, useWatch, type FieldError } from 'react-hook-form';
import { cn } from '@/lib/cn';
import { CHOICE_TONE, ChoiceChips, FieldHelp } from '@/shared/patterns/EditorForm';
import { REFERRAL_PROMOTION_HELP } from '../constants/helpText';
import type { ScopeChipTone } from '@/shared/patterns/ScopeChips';
import type { ReferralChannel } from '../api/promotionsContract';
import type { ReferralPromotionFormValues } from '../lib/referralPromotionForm';
import { REWARD_OPTIONS, type ReferralRole } from '../lib/referralPromotionOptions';
import { referralRewardFormText } from '../lib/referralPromotionText';
import { FieldMessage, INLINE_INPUT, Unit } from './editorParts';

/** Los chips de elegir van en violeta, como el `data-tone="promo"` del mock. */
export const PROMO_TONE: ScopeChipTone = CHOICE_TONE;

const FIELD = 'flex flex-col gap-1';
const FIELD_LABEL = 'text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground';
const NOTE = 'text-[10.5px] text-muted-foreground';

/** Opciones excluyentes: el control común de los editores. */
export const PickChips = ChoiceChips;

/** Campo en línea con su etiqueta arriba y la unidad al lado. */
function InlineField({
    label,
    help,
    srSuffix,
    unit,
    error,
    children,
}: {
    label: string;
    help: string;
    srSuffix: string;
    unit: ReactNode;
    error: FieldError | undefined;
    children: (props: { id: string; 'aria-invalid'?: true }) => ReactNode;
}) {
    const id = useId();
    return (
        <div className={FIELD}>
            <span className="flex items-center gap-1">
                <label htmlFor={id} className={FIELD_LABEL}>
                    {label}
                    {srSuffix ? <span className="sr-only">{srSuffix}</span> : null}
                </label>
                <FieldHelp label={`${label}${srSuffix}`} help={help} />
            </span>
            <span className="flex items-center gap-1">
                {children(error ? { id, 'aria-invalid': true } : { id })}
                <Unit>{unit}</Unit>
            </span>
            <FieldMessage message={error?.message} />
        </div>
    );
}

export function ReferralRewardFields({
    holder: role,
    channel,
}: {
    /** De quién es el premio. */
    holder: ReferralRole;
    channel: ReferralChannel;
}) {
    const {
        control,
        register,
        setValue,
        formState: { errors },
    } = useFormContext<ReferralPromotionFormValues>();
    const reward = useWatch({ control, name: role });
    const fieldErrors = errors[role];
    const referred = role === 'referredReward';
    const srSuffix = referred ? ' del recomendado' : '';
    const options = REWARD_OPTIONS[channel][role];
    const isPercent = reward.type === 'PERCENT_BONUS' || reward.type === 'SALE_DISCOUNT';
    const toPCF = reward.type === 'FIXED_BONUS' || reward.type === 'PERCENT_BONUS';
    const couponUnit = reward.couponType === 'PERCENTAGE' ? '%' : '$ MXN';

    return (
        <div className="flex flex-col gap-2">
            <PickChips
                label={referred ? 'Premio del recomendado' : 'Premio de quien recomienda'}
                options={options}
                value={reward.type}
                onPick={(type) => {
                    setValue(`${role}.type`, type, { shouldDirty: true, shouldValidate: false });
                }}
            />
            <FieldMessage message={fieldErrors?.type?.message} />

            <div className="flex flex-wrap items-start gap-3">
                {reward.type === 'FIXED_BONUS' ? (
                    <InlineField
                        label="Bono"
                        help={REFERRAL_PROMOTION_HELP.rewardAmount}
                        srSuffix={srSuffix}
                        unit="$ MXN"
                        error={fieldErrors?.amount}
                    >
                        {(invalid) => (
                            <input
                                inputMode="decimal"
                                placeholder={referred ? 'ej. 100' : 'ej. 150'}
                                className={cn(INLINE_INPUT, 'w-[90px]')}
                                {...invalid}
                                {...register(`${role}.amount`)}
                            />
                        )}
                    </InlineField>
                ) : null}
                {isPercent ? (
                    <>
                        <InlineField
                            label={reward.type === 'SALE_DISCOUNT' ? 'Descuento' : 'Porcentaje'}
                            help={REFERRAL_PROMOTION_HELP.rewardPercent}
                            srSuffix={srSuffix}
                            unit={reward.type === 'SALE_DISCOUNT' ? '% en su equipo' : '%'}
                            error={fieldErrors?.percent}
                        >
                            {(invalid) => (
                                <input
                                    inputMode="decimal"
                                    placeholder="ej. 10"
                                    className={INLINE_INPUT}
                                    {...invalid}
                                    {...register(`${role}.percent`)}
                                />
                            )}
                        </InlineField>
                        <InlineField
                            label={referred ? 'Tope' : 'Tope por premio'}
                            help={REFERRAL_PROMOTION_HELP.rewardCap}
                            srSuffix={srSuffix}
                            unit="$ MXN"
                            error={fieldErrors?.maxAmount}
                        >
                            {(invalid) => (
                                <input
                                    inputMode="decimal"
                                    placeholder="Sin tope"
                                    className={cn(INLINE_INPUT, 'w-[90px]')}
                                    {...invalid}
                                    {...register(`${role}.maxAmount`)}
                                />
                            )}
                        </InlineField>
                    </>
                ) : null}
                {reward.type === 'COUPON' ? (
                    <>
                        <div className={FIELD}>
                            <span className="flex items-center gap-1">
                                <span className={FIELD_LABEL}>Tipo de cupón</span>
                                <FieldHelp
                                    label={`Tipo de cupón${srSuffix}`}
                                    help={REFERRAL_PROMOTION_HELP.couponType}
                                />
                            </span>
                            <PickChips
                                label={`Tipo de cupón${srSuffix}`}
                                options={[
                                    { value: 'PERCENTAGE', label: '% de descuento' },
                                    { value: 'AMOUNT', label: '$ de descuento' },
                                ]}
                                value={reward.couponType}
                                onPick={(type) => {
                                    setValue(`${role}.couponType`, type, { shouldDirty: true });
                                }}
                            />
                        </div>
                        <InlineField
                            label="Valor"
                            help={REFERRAL_PROMOTION_HELP.couponValue}
                            srSuffix={srSuffix}
                            unit={couponUnit}
                            error={fieldErrors?.couponValue}
                        >
                            {(invalid) => (
                                <input
                                    inputMode="decimal"
                                    placeholder="ej. 10"
                                    className={INLINE_INPUT}
                                    {...invalid}
                                    {...register(`${role}.couponValue`)}
                                />
                            )}
                        </InlineField>
                        <InlineField
                            label="Vence en"
                            help={REFERRAL_PROMOTION_HELP.couponDays}
                            srSuffix={srSuffix}
                            unit="días"
                            error={fieldErrors?.couponDays}
                        >
                            {(invalid) => (
                                <input
                                    inputMode="numeric"
                                    placeholder="30"
                                    className={INLINE_INPUT}
                                    {...invalid}
                                    {...register(`${role}.couponDays`)}
                                />
                            )}
                        </InlineField>
                    </>
                ) : null}
            </div>

            {toPCF ? (
                <p className={NOTE}>
                    Se abona en su <b className="text-foreground">cuenta PCF</b>.
                </p>
            ) : null}
            {reward.type === 'COUPON' ? (
                <p className={NOTE}>
                    {referred
                        ? 'El cupón le llega cuando su compra queda cobrada.'
                        : 'El cupón le llega cuando la compra de su referido queda cobrada.'}
                </p>
            ) : null}
            {reward.type === 'SALE_DISCOUNT' ? (
                <p className={NOTE}>Se aplica en esta misma venta, antes de cobrar.</p>
            ) : null}
            <p className="text-[11px]">
                <b className="font-semibold text-foreground">{referralRewardFormText(reward)}</b>
            </p>
        </div>
    );
}
